import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';
import { connect } from 'react-redux';
import produce from 'immer';

import {
  faceHighlightsUpdate,
  albumHighlightCreate,
  albumHighlightDelete,
} from '../../../../../graphql/mutations/albums';
import {
  PhotoForHighlights,
  AlbumHighlights,
} from '../../../../../graphql/queries/albums';
import {
  AlbumPhotoFaceHighlights,
  AlbumFaceHighlightedPhotos,
} from '../../../../../graphql/fragments/album';
import { withContributorPermissions } from '../../../services/contributors';

const withFaceHighlightsUpdate = graphql(faceHighlightsUpdate, {
  props: ({
    mutate,
    ownProps: {
      match,
      photoRecord,
      filteredIdentities,
      filter,
      albumId,
      albumPhoto,
      faceHighlights,
      faces: photoFaces,
    },
  }) => ({
    faceHighlightsUpdate: (faces, albumPhotoPhotoId, albumPhotoAlbumId) =>
      mutate({
        variables: {
          input: {
            album_id:
              albumPhotoAlbumId ||
              albumPhoto?.album?.id ||
              albumId ||
              match?.params?.albumId,
            photo_id:
              albumPhotoPhotoId ||
              albumPhoto?.photo?.id ||
              photoRecord?.id ||
              match?.params.photoId,
            faces: faces.map(face => ({
              id: face.id,
              rating: face.rating,
            })),
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          faceHighlightsUpdate: {
            __typename: 'FaceHighlightsUpdatePayload',
            face_highlights: faces.map((face, index) => ({
              __typename: 'FaceHighlight',
              id: index,
              rating: face?.rating ?? 1,
              face: { id: face.id, ...face, __typename: 'Face' },
              album: {
                id: albumPhoto?.album?.id || albumId || match?.params?.albumId,
                __typename: 'Album',
              },
            })),
          },
        },
        update: async (store, { data: { faceHighlightsUpdate: response } }) => {
          const photoId =
            albumPhotoPhotoId ||
            albumPhoto?.photo?.id ||
            photoRecord?.id ||
            match?.params.photoId;
          const activeAlbumId =
            albumPhotoAlbumId ||
            albumPhoto?.album?.id ||
            albumId ||
            match?.params?.albumId;
          const isNewHighlight =
            faceHighlights.length === 0 && photoFaces.length !== 0;

          try {
            const data = store.readQuery({
              query: PhotoForHighlights,
              variables: {
                id: photoId,
                albumId: activeAlbumId,
              },
            });

            const updatedData = produce(data, draft => {
              draft.photo.face_highlights.records = response.face_highlights;
            });

            store.writeQuery({
              query: PhotoForHighlights,
              variables: {
                id: photoId,
                albumId: activeAlbumId,
              },
              data: updatedData,
            });
          } catch (error) {
            // eslint-disable-next-line
          }

          if (albumPhoto?.id) {
            try {
              const albumPhotoData = store.readFragment({
                fragment: AlbumPhotoFaceHighlights,
                id: defaultDataIdFromObject(albumPhoto),
                fragmentName: 'AlbumPhotoFaceHighlights',
              });

              albumPhotoData.face_highlights.records = response.face_highlights;

              store.writeFragment({
                fragment: AlbumPhotoFaceHighlights,
                fragmentName: 'AlbumPhotoFaceHighlights',
                id: defaultDataIdFromObject(albumPhoto),
                data: albumPhotoData,
              });
            } catch (error) {
              // eslint-disable-next-line
            }
          }

          if (faces.length === 0 || isNewHighlight) {
            // Update fragment:
            try {
              const albumFragment = store.readFragment({
                id: defaultDataIdFromObject({
                  id: activeAlbumId,
                  __typename: 'Album',
                }),
                fragment: AlbumFaceHighlightedPhotos,
              });

              if (isNewHighlight) {
                albumFragment.face_highlighted_photos.total += 1;
              } else {
                albumFragment.face_highlighted_photos.total -= 1;
              }

              store.writeFragment({
                id: defaultDataIdFromObject({
                  id: activeAlbumId,
                  __typename: 'Album',
                }),
                fragment: AlbumFaceHighlightedPhotos,
                data: albumFragment,
              });
            } catch (error) {
              // eslint-disable-next-line
            }
          }

          if (faces.length === 0) {
            // Update the album highlights query:
            try {
              const highlightsData = store.readQuery({
                query: AlbumHighlights,
                variables: {
                  id: activeAlbumId,
                  identityIds: filteredIdentities,
                  favoriteFilter: filter.favoriteFilter,
                  ratingFilter:
                    filter.ratingFilter > 0 ? [filter.ratingFilter] : [],
                },
              });

              const updatedHighlightsData = produce(highlightsData, draft => {
                draft.album.face_highlighted_photos.total -= 1;
                draft.album.face_highlighted_photos.records = highlightsData.album.face_highlighted_photos.records.filter(
                  faceHighlight => faceHighlight.photo.id !== photoId,
                );
              });

              store.writeQuery({
                query: AlbumHighlights,
                variables: {
                  id: activeAlbumId,
                  identityIds: filteredIdentities,
                  favoriteFilter: filter.favoriteFilter,
                  ratingFilter:
                    filter.ratingFilter > 0 ? [filter.ratingFilter] : [],
                },
                data: updatedHighlightsData,
              });
            } catch (error) {
              // eslint-disable-next-line
              // We might get an error saying photo was not found.
              // This because the query is not necessarily loaded.
            }
          }
        },
      }),
  }),
});

const withAlbumHighlightCreateMutation = graphql(albumHighlightCreate, {
  props: ({ mutate, ownProps }) => ({
    createHighlight: (albumId, photo) =>
      mutate({
        variables: {
          input: {
            albums_uuid: albumId,
            photos_uuid: photo.id,
            display_order: 0,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          albumHighlightCreate: {
            __typename: 'AlbumHighlight',
            album_highlight: {
              __typename: 'AlbumHighlight',
              id: Date.now().toString(),
              photo,
            },
          },
        },
        update: (store, { data: { albumHighlightCreate: response } }) => {
          const { match, albumPhoto, filter, filteredIdentities } = ownProps;

          const activeAlbumId =
            albumPhoto?.album.id || albumId || match?.params.albumId;

          const data = store.readQuery({
            query: AlbumHighlights,
            variables: {
              id: activeAlbumId,
              identityIds: filteredIdentities,
              favoriteFilter: filter.favoriteFilter,
              ratingFilter:
                filter.ratingFilter > 0 ? [filter.ratingFilter] : [],
            },
          });

          const updatedData = produce(data, draft => {
            draft.album.highlights.push(response.album_highlight);
          });

          store.writeQuery({
            query: AlbumHighlights,
            variables: {
              id: activeAlbumId,
              identityIds: filteredIdentities,
              favoriteFilter: filter.favoriteFilter,
              ratingFilter:
                filter.ratingFilter > 0 ? [filter.ratingFilter] : [],
            },
            data: updatedData,
          });
        },
      }),
  }),
});

const withAlbumHighlightDeleteMutation = graphql(albumHighlightDelete, {
  props: ({ mutate, ownProps }) => ({
    deleteHighlight: highlightId =>
      mutate({
        variables: {
          input: {
            id: highlightId,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          albumHighlightDelete: {
            __typename: 'AlbumHighlight',
            deleted: true,
          },
        },
        update: store => {
          const {
            match,
            albumId,
            albumPhoto,
            filter,
            filteredIdentities,
          } = ownProps;

          const activeAlbumId =
            albumPhoto?.album.id || albumId || match?.params.albumId;

          const data = store.readQuery({
            query: AlbumHighlights,
            variables: {
              id: activeAlbumId,
              identityIds: filteredIdentities,
              favoriteFilter: filter.favoriteFilter,
              ratingFilter:
                filter.ratingFilter > 0 ? [filter.ratingFilter] : [],
            },
          });

          const updatedData = produce(data, draft => {
            draft.album.highlights = data.album.highlights.filter(
              highlight => highlight.id !== highlightId,
            );
          });

          store.writeQuery({
            query: AlbumHighlights,
            variables: {
              id: activeAlbumId,
              identityIds: filteredIdentities,
              favoriteFilter: filter.favoriteFilter,
              ratingFilter:
                filter.ratingFilter > 0 ? [filter.ratingFilter] : [],
            },
            data: updatedData,
          });
        },
      }),
  }),
});

const withAlbumHighlights = graphql(AlbumHighlights, {
  options: ({ match, albumId, albumPhoto, filteredIdentities, filter }) => ({
    variables: {
      id: albumPhoto?.album?.id || albumId || match?.params.albumId,
      albumId: albumPhoto?.album?.id || albumId || match?.params.albumId,
      identityIds: filteredIdentities,
      favoriteFilter: filter.favoriteFilter,
      ratingFilter: filter.ratingFilter > 0 ? [filter.ratingFilter] : [],
    },
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data: { album } }) => ({
    albumHighlights: album?.highlights ?? [],
  }),
  skip: ({ isContributor }) => isContributor,
});

const mapStateToProps = ({ highlights }) => ({
  filteredIdentities: highlights.identities,
  filter: highlights.filter,
});

export default compose(
  withRouter,
  withContributorPermissions(({ match, albumId, albumPhoto }) => ({
    entityId: albumPhoto?.album?.id || albumId || match?.params?.albumId,
  })),
  connect(mapStateToProps),
  withAlbumHighlights,
  withAlbumHighlightCreateMutation,
  withAlbumHighlightDeleteMutation,
  withFaceHighlightsUpdate,
);
